/**
 * Per-segment highlight colors for the save-for-later → combine flow.
 *
 * Each saved segment gets its own color (persisted on the group) so the
 * transcript highlight, the context-menu submenu, and the header filter all
 * share one consistent indicator per segment. The palette avoids pure blues
 * (quotes/overlays underline) and emerald (clips border) so a saved-segment
 * highlight still reads as its own family.
 *
 * Index 0 is the original violet (`--content-saved-segments`) so the first
 * segment keeps the look segments shipped with, and colorless segments saved
 * before this change fall back to it for display.
 *
 * ## Why seven
 *
 * Fencing off blues (200–235°) and greens (140–168°) leaves ~297° of usable
 * hue. A highlight renders as a translucent wash over dark text, and washing
 * pulls colors toward each other — two hues need roughly 30° of separation to
 * stay tellable apart once washed. That caps the palette at about seven;
 * packing more in re-creates the near-duplicate pairs this palette used to have
 * (amber 43° vs orange 27° read as the same color).
 */
export const SAVED_SEGMENT_COLOR_PALETTE: string[] = [
  '#a78bfa', // violet 255° (matches --content-saved-segments)
  '#f472b6', // pink 330°
  '#fbbf24', // amber 43°
  '#2dd4bf', // teal 174°
  '#a3e635', // lime 82°
  '#f87171', // red 0°
  '#e879f9', // fuchsia 289°
];

export const DEFAULT_SAVED_SEGMENT_COLOR = SAVED_SEGMENT_COLOR_PALETTE[0];

/**
 * Human names for the palette, used for swatch labels, highlight names and
 * tooltips. A bare hex is useless to a screen reader. Colors outside the
 * palette (persisted on older segments) fall back to the hex via
 * `savedSegmentColorName`.
 */
const SAVED_SEGMENT_COLOR_NAMES: Record<string, string> = {
  '#a78bfa': 'Violet',
  '#f472b6': 'Pink',
  '#fbbf24': 'Amber',
  '#2dd4bf': 'Teal',
  '#a3e635': 'Lime',
  '#f87171': 'Red',
  '#e879f9': 'Fuchsia',
};

/** Display name for a highlight color, falling back to the raw value. */
export function savedSegmentColorName(color: string): string {
  return SAVED_SEGMENT_COLOR_NAMES[color.toLowerCase()] ?? color;
}

/**
 * Default name for a new highlight — its color, not its text ("Violet
 * Highlight"). The transcript text a highlight covers is already shown on
 * hover, and a 60-char slice of it made every row in the submenu read as a
 * wall of near-identical quotes; the color is what the user actually picked
 * and what the swatch, underline and filter row all show. Renaming overwrites
 * this, so a user label still wins.
 */
export function savedSegmentDefaultName(color: string): string {
  return `${savedSegmentColorName(color)} Highlight`;
}

/**
 * Pick the next distinct palette color given the colors already in use. Prefers
 * the first unused palette entry; once every color is taken it cycles by count
 * so new segments still get a deterministic, evenly-distributed color.
 */
export function pickNextSavedSegmentColor(existingColors: string[]): string {
  const used = new Set(existingColors);
  const unused = SAVED_SEGMENT_COLOR_PALETTE.find(color => !used.has(color));
  if (unused) return unused;
  return SAVED_SEGMENT_COLOR_PALETTE[
    existingColors.length % SAVED_SEGMENT_COLOR_PALETTE.length
  ];
}

/**
 * How strongly a highlight color is washed behind transcript text. Every
 * palette color sits at one brightness, so one alpha covers them all — it
 * matches the `--content-saved-segments-bg` token that colorless highlights
 * still fall back to.
 */
export const SAVED_SEGMENT_WASH_ALPHA = 0.35;
